<template>
    <div>
        <Modal
            :show="modalVisibility"
            :overflowHidden="false"
            class="overflow-visible"
            @close="closeModal"
        >
            <div class="flex flex-col">
                <h1 class="text-body-2 mt-3 mb-2">Add file</h1>
                <div class="xxs:px-0 py-4 bg-white rounded border border-white lg:px-0 lg:pt-4 lg:pb-0 box-border w-full">
                    <div
                        class="w-full min-h-[400px] border-dashed border rounded flex items-center justify-center"
                        style="border-spacing: 5px"
                        :class="[isDragging ? 'border-primary-1' : 'border-default-4', uploadHasError && 'border-red-500']"
                        @dragover.prevent="handleDragOver"
                        @dragleave="handleDragLeave"
                        @drop.prevent="handleDrop"
                    >
                        <div v-if="uploadInProgress" class="flex flex-col items-center justify-center">
                            <Spinner class="w-8 h-8" :color="color.hex"/>
                            <span class="text-primary-1 text-body-6">Uploading...</span>
                        </div>
                        <div v-if="uploadComplete" class="flex flex-col items-center justify-center">
                            <Check class="w-8 h-8 mb-4" :color="color.hex" />
                            <span class="text-primary-1 text-body-6">Upload complete</span>
                        </div>
                        <div v-if="uploadHasError && !isDragging" class="flex flex-col items-center justify-center">
                            <Close class="w-8 h-8 mb-4" :color="fullConfig.theme.colors.primary[2]" />
                            <span class="text-primary-2 text-body-6 text-center px-4">{{ uploadHasError }}</span>
                        </div>
                        <div v-if="!isDragging && !uploadInProgress && !uploadComplete && !uploadHasError" class="flex flex-col items-center justify-center">
                            <span class="text-primary-1 text-body-6">Drag and drop or upload new document</span>
                            <span class="text-default-1 text-body-7">jpg, png, pdf, xlsx, xls, doc or docx up to 25 Mb</span>
                            <Button
                                class="mt-6 scale-75"
                                buttonType="secondary"
                                @click="triggerFileInput">
                                or browse files from your computer
                            </Button>

                            <div
                                class="text-default-1 text-body-7 mt-6 xxs:px-3 text-center"
                            >The file will be uploaded to the current folder you are in</div>
                        </div>
                        <div v-if="isDragging && !uploadInProgress && !uploadComplete" class="flex flex-col items-center justify-center">
                            <span class="text-primary-1 text-body-6">Release the mouse to upload the file</span>
                        </div>
                    </div>
                    <input type="file" ref="fileInput" style="display: none;" @change="handleFileInputChange" multiple />

                    <Button
                        :disabled="!fileInput"
                        class="xxs:w-full lg:w-auto self-start mt-6"
                        buttonType="ghost"
                        :loading="loading"
                        @click="closeModal"
                    >Done</Button>

                </div>
            </div>
        </Modal>
        <UploadsHistory v-if="successfullyUploadedFiles.length > 0" :files="successfullyUploadedFiles" />
    </div>
</template>

<script setup>
import {ref} from 'vue';
import Modal from "@/components/core/Modal.vue";
import Button from "@/components/core/Button.vue";
import {useDocuments} from '@/composables/useDocuments'
import {useAuth} from "@/stores/AuthStore";
import {storeToRefs} from "pinia";
import Spinner from "@/components/icons/Spinner.vue";
import {toast} from "vue3-toastify";
import Check from "@/components/icons/Check.vue";
import Close from "@/components/icons/Close.vue";
import tailwindConfig from '@/../tailwind.config';
import resolveConfig from "tailwindcss/resolveConfig";
import {useTheme} from "@/stores/ThemeStore";
import UploadsHistory from "@/components/custom/documents/UploadsHistory.vue";
import debounce from "lodash.debounce";

const emit = defineEmits(['close', 'file-uploaded']);

const props = defineProps({
    modalVisibility: {
        type: Boolean,
        default: false,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    currentFolder: {
        type: [Object, null],
        default: null,
    }
});

const {textColorClass} = useTheme();

const fullConfig = resolveConfig(tailwindConfig)

const {organization} = storeToRefs(useAuth());
const {createFile} = useDocuments();
const {color} = useTheme();

const uploadInProgress = ref(false);
const uploadComplete = ref(false);
const uploadHasError = ref(null);
const uploadMessageTimeout = ref(null);

const isDragging = ref(false);
const fileInput = ref(null);
const errorMessage = ref('');

// Maximum file size: 25 MB in bytes
const MAX_FILE_SIZE = 25 * 1024 * 1024;
const ALLOWED_FILE_TYPES = [
    'image/jpeg',
    'image/png',
    'application/pdf',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // For Excel (.xlsx)
    'application/vnd.ms-excel', // For older Excel formats (.xls)
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // For Word (.docx)
    'application/msword' // For older Word formats (.doc)
];

const successfullyUploadedFiles = ref([]);

const validateFile = (file) => {
    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
        uploadHasError.value = 'The file must be a file of type: jpg, png, pdf, xlsx, xls, docx or doc';
        return false;
    }

    if (file.size > MAX_FILE_SIZE) {
        uploadHasError.value = 'File size exceeds 25MB';
        return false;
    }

    // Clear the error if validation passes
    errorMessage.value = '';
    return true;
};

const handleDragOver = () => {
    isDragging.value = true;
};

const handleDragLeave = () => {
    isDragging.value = false;
};

const handleDrop = (e) => {
    isDragging.value = false;

    if (e.dataTransfer.files.length) {
        const dataTransfer = new DataTransfer();

        // Loop through all dropped files and add them to the DataTransfer object
        Array.from(e.dataTransfer.files).forEach(file => {
            dataTransfer.items.add(file);
        });

        // Assign all the dropped files to the file input
        fileInput.value.files = dataTransfer.files;

        handleUpload();
    }
};

const handleUploadError = (message) => {
    uploadHasError.value = message;

    uploadMessageTimeout.value = setTimeout(() => {
        uploadHasError.value = null;
    }, 100000);

    toast.error(message);
};


const handleUpload = async () => {
    const filesUploadedUID = [];
    if (fileInput.value.files.length > 0) {
        let response = null;

        let index = 0;
        for (const file of fileInput.value.files) {
            if (!validateFile(file)) {
                toast.error(uploadHasError.value);
                continue;
            }

            let data = new FormData();

            clearTimeout(uploadMessageTimeout.value);
            uploadComplete.value = false;
            uploadHasError.value = null;

            try {
                uploadInProgress.value = true;

                data.append("file", file);
                if (props.currentFolder) {
                    data.append("folder_uid", props.currentFolder.uid);
                }

                response = await createFile(organization.value.slug, data);
                filesUploadedUID.push(response.uid);

                successfullyUploadedFiles.value.unshift(response);

                uploadInProgress.value = false;
                uploadComplete.value = true;

                uploadMessageTimeout.value = setTimeout(() => {
                    uploadComplete.value = false;
                }, 3000);
            } catch (error) {
                switch (error.response.status) {
                    case 409:
                        handleUploadError(error.response.data.message);
                        break;
                    case 413:
                        handleUploadError(error.response.data.message);
                        break;
                    case 422:
                        handleUploadError(error.response.data.message);
                        break;
                    default:
                        handleUploadError("We have encountered an error while uploading the file. Please try again later.");
                        break;
                }
            } finally {
                uploadInProgress.value = false;
                index++;
            }
        }

        if (index === fileInput.value.files.length) {
            emit('file-uploaded');
        }
    }

    return filesUploadedUID;
}

function triggerFileInput() {
    fileInput.value.click();
}

function handleFileInputChange(e) {
    const input = e.target;
    if (input.files && input.files.length > 0) {
        handleUpload();
    }
}

function closeModal() {
    if (uploadInProgress.value) {
        return;
    }

    uploadInProgress.value = false;
    uploadComplete.value = false;
    uploadHasError.value = null;
    successfullyUploadedFiles.value = [];

    clearTimeout(uploadMessageTimeout.value);

    emit('close')
}
</script>
